import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import HelpIcon from '../../../components/help/HelpIcon';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { PageContainer } from '../components/AdminContentWrapper';
import AgentsTable from '../components/AgentsTable';
import AgentDetailsDialog from '../components/AgentDetailsDialog';
import KnowledgeBaseAdminDialog from '../components/KnowledgeBaseAdminDialog';
import TablePaginationBar from '../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../hooks';
import UserProfileService from '../../../services/UserProfileService';
import SemanticWorkspaceService from '../../../services/SemanticWorkspaceService';
import SemanticModelService from '../../../services/SemanticModelService';
import AgentService from '../../../services/AgentService';
import KnowledgeBaseService from '../../../services/KnowledgeBaseService';

// El fondo de esta sección es oscuro (#121827) pero MUI pinta el texto de
// los TextField/Select en negro por defecto — sin esto, el texto quedaba
// invisible sobre el fondo oscuro.
const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': {
      borderColor: 'var(--wb-outline)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--wb-text-dim)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--wb-text-soft)',
  },
  '& .MuiFormHelperText-root': {
    color: 'var(--wb-text-dim)',
  },
};

const darkInputLabelStyle = {
  color: 'var(--wb-text-soft)',
};

const darkSelectStyle = {
  color: 'var(--wb-text-strong)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--wb-outline)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--wb-text-dim)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--wb-primary)',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--wb-text-strong)',
  },
};

const EMPTY_FORM_VALUES = {
  workspaceId: '',
  modelId: '',
  agentName: '',
  instruction: '',
  knowledgeBaseId: ''
};

const resolveUserId = (profile) =>
  profile?.user_id || profile?.userId || profile?.user_uuid || profile?.sub || null;

const resolveAccountId = (profile) =>
  profile?.account_id ||
  profile?.accountId ||
  profile?.account_uuid ||
  profile?.accountUuid ||
  null;

const resolveCompanyName = (profile) =>
  profile?.company_name ||
  profile?.companyName ||
  profile?.company?.name ||
  profile?.company?.legal_name ||
  '';

const normalizeModelRecord = (record, fallbackId) => {
  if (!record || typeof record !== 'object') {
    return {
      semantic_model_id: fallbackId,
      name: fallbackId,
      description: 'No fue posible recuperar los detalles del modelo.'
    };
  }

  const semanticModelId =
    record.semantic_model_id ||
    record.semanticModelId ||
    record.id ||
    record.ID ||
    fallbackId;

  return {
    semantic_model_id: semanticModelId,
    name: record.name || record.model_name || semanticModelId,
    description: record.description || ''
  };
};

const AgentsOverview = () => {
  const [profile, setProfile] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceModels, setWorkspaceModels] = useState({});
  const [agents, setAgents] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);

  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [isDeletingAgent, setIsDeletingAgent] = useState(false);

  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastUpdated, setLastUpdated] = useState(null);

  const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);

  // null = modo "crear agente"; un agente = modo "editar agente".
  const [editingAgent, setEditingAgent] = useState(null);
  // true mientras se confirma reasignar el workspace de un agente existente
  // (cambia qué modelos/conexiones puede usar, así que se pide confirmación
  // explícita antes de guardar).
  const [confirmReassignOpen, setConfirmReassignOpen] = useState(false);
  // Agente ACTIVE que sería reemplazado al crear/guardar otro para el mismo
  // (workspace, modelo). Mientras no sea null se muestra el diálogo de aviso.
  const [replaceTarget, setReplaceTarget] = useState(null);
  // Agente SUPERSEDED que se puede reactivar tras borrar el ACTIVE de su modelo.
  const [reactivateCandidate, setReactivateCandidate] = useState(null);
  const [reactivatingId, setReactivatingId] = useState(null);

  const [agentToDelete, setAgentToDelete] = useState(null);
  const [agentToView, setAgentToView] = useState(null);
  const [kbRefToView, setKbRefToView] = useState(null);

  const currentUserId = useMemo(() => resolveUserId(profile), [profile]);
  const currentAccountId = useMemo(() => resolveAccountId(profile), [profile]);
  const companyName = useMemo(() => resolveCompanyName(profile) || 'mi_empresa', [profile]);

  const modelsForWorkspace = formValues.workspaceId
    ? workspaceModels[formValues.workspaceId] || []
    : [];

  const loadAgents = useCallback(
    async () => {
      if (!currentAccountId) {
        return;
      }
      try {
        setIsLoadingAgents(true);
        // account_id NO se manda: agents-admin lo deriva del JWT y filtra
        // internamente, sin necesidad de pasarlo por query string.
        // listAllAgents (recorre todas las páginas) y no listAgents: esta
        // pantalla NO puede trabajar con un subconjunto. `activeAgentFor`
        // avisa "crear este reemplaza al que ya cubre ese modelo", y tras
        // borrar un ACTIVE se busca un SUPERSEDED del mismo (workspace,
        // modelo) para ofrecer reactivarlo. Los dos son búsquedas sobre el
        // conjunto ENTERO: con una página, el aviso no aparece y el agente
        // reemplazado queda invisible, sin nada que lo indique.
        //
        // La paginación de acá abajo es de presentación: se recorre todo y
        // se muestra de a tandas.
        const response = await AgentService.listAllAgents();
        setAgents(response.agents || []);
        setLastUpdated(new Date());
      } catch (err) {
        console.error('AgentsOverview.loadAgents: error', err);
        setError(err.message || 'No fue posible obtener los agentes.');
      } finally {
        setIsLoadingAgents(false);
      }
    },
    [currentAccountId]
  );

  const loadWorkspaceModels = useCallback(
    async (workspaceId) => {
      if (!workspaceId || workspaceModels[workspaceId]) {
        return;
      }
      try {
        setIsLoadingModels(true);
        const ids = await SemanticWorkspaceService.getModels(workspaceId);
        const details = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await SemanticModelService.getById(id);
              return normalizeModelRecord(response, id);
            } catch (err) {
              console.error('AgentsOverview.loadWorkspaceModels: error obteniendo modelo', id, err);
              return normalizeModelRecord(null, id);
            }
          })
        );
        setWorkspaceModels((prev) => ({
          ...prev,
          [workspaceId]: details
        }));
      } catch (err) {
        console.error('AgentsOverview.loadWorkspaceModels: error', err);
        setSnackbar({
          open: true,
          message: err.message || 'No fue posible obtener los modelos vinculados al workspace.',
          severity: 'error'
        });
      } finally {
        setIsLoadingModels(false);
      }
    },
    [workspaceModels]
  );

  const loadWorkspaces = useCallback(async () => {
    if (!currentUserId) {
      return;
    }
    try {
      setIsLoadingWorkspaces(true);
      const response = await SemanticWorkspaceService.listByUser(currentUserId, {
        includeDeleted: false
      });
      const availableWorkspaces = (response.items || []).filter(
        (item) => !item?.soft_delete?.deleted
      );
      setWorkspaces(availableWorkspaces);
      if (availableWorkspaces.length > 0) {
        setFormValues((prev) => ({
          ...prev,
          workspaceId: prev.workspaceId || availableWorkspaces[0].semantic_workspace_id || ''
        }));
      }
    } catch (err) {
      console.error('AgentsOverview.loadWorkspaces: error', err);
      setError(err.message || 'No fue posible obtener los espacios de análisis.');
    } finally {
      setIsLoadingWorkspaces(false);
    }
  }, [currentUserId]);

  const loadProfile = useCallback(async () => {
    try {
      setIsLoadingProfile(true);
      setError(null);
      const userProfile = await UserProfileService.getProfile();
      setProfile(userProfile);
    } catch (err) {
      console.error('AgentsOverview.loadProfile: error', err);
      setError(err.message || 'No pudimos obtener el perfil del usuario.');
    } finally {
      setIsLoadingProfile(false);
    }
  }, []);

  const loadKnowledgeBases = useCallback(async () => {
    try {
      const list = await KnowledgeBaseService.listKnowledgeBases();
      setKnowledgeBases(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('AgentsOverview.loadKnowledgeBases: error', err);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadKnowledgeBases();
  }, [loadKnowledgeBases]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  useEffect(() => {
    if (formValues.workspaceId) {
      loadWorkspaceModels(formValues.workspaceId);
    }
  }, [formValues.workspaceId, loadWorkspaceModels]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handleRefresh = () => {
    loadWorkspaces();
    loadAgents();
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setFormValues({
      workspaceId: agent.semanticWorkspaceId || '',
      modelId: agent.semanticModelId || '',
      agentName: agent.agentName || '',
      instruction: agent.instruction || '',
      knowledgeBaseId: agent.knowledgeBaseRef || ''
    });
    if (agent.semanticWorkspaceId) {
      loadWorkspaceModels(agent.semanticWorkspaceId);
    }
  };

  const handleCancelEdit = () => {
    setEditingAgent(null);
    setFormValues(EMPTY_FORM_VALUES);
  };

  const performCreateAgent = async () => {
    // account_id/user_id ya NO se envían: agents-admin los deriva del JWT.
    // enabled_tools/*_connection_id se derivan del workspace. La base de
    // conocimiento se liga por `knowledge_base_ref` (id de la pantalla
    // "Bases de conocimiento"); ya NO se auto-provisiona al crear el agente.
    const payload = {
      semantic_workspace_id: formValues.workspaceId,
      semantic_model_id: formValues.modelId,
      company_name: companyName || 'mi_empresa',
      ...(formValues.agentName ? { agentName: formValues.agentName } : {}),
      ...(formValues.instruction ? { instruction: formValues.instruction } : {}),
      ...(formValues.knowledgeBaseId ? { knowledge_base_ref: formValues.knowledgeBaseId } : {})
    };

    await AgentService.createAgent(payload);
    setSnackbar({
      open: true,
      message: 'Agente creado correctamente.',
      severity: 'success'
    });
    setFormValues(EMPTY_FORM_VALUES);
  };

  const performUpdateAgent = async () => {
    // Reasignar semantic_workspace_id re-deriva enabled_tools/*_connection_id
    // contra el nuevo workspace. `knowledge_base_ref`: string para ligar,
    // null para desvincular.
    await AgentService.updateAgent(editingAgent.agentId, {
      agent_name: formValues.agentName || undefined,
      instruction: formValues.instruction || undefined,
      semantic_workspace_id: formValues.workspaceId,
      semantic_model_id: formValues.modelId || undefined,
      company_name: companyName || 'mi_empresa',
      knowledge_base_ref: formValues.knowledgeBaseId || null
    });
    setSnackbar({
      open: true,
      message: 'Agente actualizado correctamente.',
      severity: 'success'
    });
    setEditingAgent(null);
    setFormValues(EMPTY_FORM_VALUES);
  };

  const { pageRows, paginationProps } = useTablePagination(agents, {
    storageKey: 'agentsOverview'
  });

  // Agente ACTIVE que ya cubre un (workspace, modelo). Se usa para avisar
  // que crear otro lo reemplaza.
  const activeAgentFor = useCallback(
    (workspaceId, modelId, excludeId = null) =>
      agents.find(
        (a) =>
          a.agentStatus === 'ACTIVE' &&
          a.semanticWorkspaceId === workspaceId &&
          a.semanticModelId === modelId &&
          a.agentId !== excludeId
      ) || null,
    [agents]
  );

  const runSave = async () => {
    try {
      setIsSavingAgent(true);
      if (editingAgent) {
        await performUpdateAgent();
      } else {
        await performCreateAgent();
      }
      setLastUpdated(new Date());
      loadAgents();
    } catch (err) {
      console.error('AgentsOverview.runSave: error', err);
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar el agente.', severity: 'error' });
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleSaveAgent = async () => {
    if (!formValues.workspaceId) {
      setSnackbar({ open: true, message: 'Selecciona un espacio de análisis para continuar.', severity: 'error' });
      return;
    }
    if (!formValues.modelId) {
      setSnackbar({ open: true, message: 'Selecciona un modelo semántico. Cada agente se liga a un modelo.', severity: 'error' });
      return;
    }

    // Reasignar el workspace de un agente ya creado se confirma aparte.
    if (editingAgent && formValues.workspaceId !== editingAgent.semanticWorkspaceId) {
      setConfirmReassignOpen(true);
      return;
    }

    // Solo puede haber un agente ACTIVE por (workspace, modelo): avisar que
    // el nuevo reemplaza al existente (quedará inactivo, se puede reactivar).
    const clash = activeAgentFor(formValues.workspaceId, formValues.modelId, editingAgent?.agentId);
    if (clash) {
      setReplaceTarget(clash);
      return;
    }

    await runSave();
  };

  const handleConfirmReplace = async () => {
    setReplaceTarget(null);
    await runSave();
  };

  const handleReactivate = async (agent) => {
    setReactivatingId(agent.agentId);
    try {
      await AgentService.updateAgent(agent.agentId, { status: 'ACTIVE' });
      setSnackbar({ open: true, message: `Agente "${agent.agentName}" reactivado.`, severity: 'success' });
      setReactivateCandidate(null);
      await loadAgents();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo reactivar el agente.', severity: 'error' });
    } finally {
      setReactivatingId(null);
    }
  };

  const handleConfirmReassign = async () => {
    setConfirmReassignOpen(false);
    try {
      setIsSavingAgent(true);
      await performUpdateAgent();
      setLastUpdated(new Date());
      loadAgents();
    } catch (err) {
      console.error('AgentsOverview.handleConfirmReassign: error', err);
      setSnackbar({
        open: true,
        message: err.message || 'No se pudo guardar el agente.',
        severity: 'error'
      });
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleDeleteRequest = (agent) => {
    setAgentToDelete(agent);
  };

  const handleViewDetails = (agent) => {
    setAgentToView(agent);
  };

  const handleViewKnowledgeBase = (agent) => {
    setKbRefToView(agent.knowledgeBaseRef || null);
  };

  const handleConfirmDelete = async () => {
    if (!agentToDelete?.agentId) {
      console.error('AgentsOverview.handleConfirmDelete: No agentId found', agentToDelete);
      return;
    }
    try {
      setIsDeletingAgent(true);
      console.log('AgentsOverview.handleConfirmDelete: Deleting agent', agentToDelete.agentId);
      await AgentService.deleteAgent(agentToDelete.agentId);
      console.log('AgentsOverview.handleConfirmDelete: Agent deleted successfully');
      const deleted = agentToDelete;
      setAgents((prev) => prev.filter((item) => item.agentId !== deleted.agentId));
      if (editingAgent?.agentId === deleted.agentId) {
        handleCancelEdit();
      }
      setSnackbar({
        open: true,
        message: `Agente ${deleted.agentName || deleted.agentId} eliminado.`,
        severity: 'success'
      });
      setLastUpdated(new Date());

      // Si se borró el agente ACTIVE de un modelo y hay uno SUPERSEDED del
      // mismo (workspace, modelo), ofrecer reactivarlo.
      if ((deleted.agentStatus || '').toUpperCase() === 'ACTIVE') {
        const superseded = agents.find(
          (a) =>
            a.agentId !== deleted.agentId &&
            (a.agentStatus || '').toUpperCase() === 'SUPERSEDED' &&
            a.semanticWorkspaceId === deleted.semanticWorkspaceId &&
            a.semanticModelId === deleted.semanticModelId
        );
        if (superseded) setReactivateCandidate(superseded);
      }
    } catch (err) {
      console.error('AgentsOverview.handleConfirmDelete: error', err);
      setSnackbar({
        open: true,
        message: err.message || 'No se pudo eliminar el agente.',
        severity: 'error'
      });
    } finally {
      setIsDeletingAgent(false);
      setAgentToDelete(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const isLoading = isLoadingProfile || isLoadingWorkspaces || isLoadingAgents;

  return (
    <PageContainer
      title="Agentes"
      description="Define agentes conversacionales, asigna capacidades y vincula sus bases de conocimiento desde tus espacios de análisis."
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
            sx={{
              borderColor: 'var(--wb-x-border-60a5fa66, rgba(96, 165, 250, 0.4))',
              color: 'var(--wb-x-text-60a5fa, #60a5fa)',
              '&:hover': { borderColor: 'var(--wb-x-border-60a5fa, #60a5fa)' }
            }}
          >
            Actualizar
          </Button>
        </Stack>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      {!error && !currentUserId && !isLoading && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Usuario no identificado</AlertTitle>
          No pudimos determinar el usuario actual. Verifica tu sesión e intenta nuevamente.
        </Alert>
      )}

      <Box
        sx={{
          backgroundColor: 'var(--wb-x-bg-121827, #121827)',
          borderRadius: 2,
          border: '1px solid var(--wb-x-border-94a3b81f, rgba(148, 163, 184, 0.12))',
          p: { xs: 2, sm: 3 },
          mb: 3
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ color: 'var(--wb-x-text-e2e8f0, #e2e8f0)', fontWeight: 600 }}>
              {editingAgent
                ? `Editar agente: ${editingAgent.agentName || editingAgent.agentId}`
                : 'Alta de agente con base de conocimiento'}
            </Typography>
            <Typography sx={{ color: 'var(--wb-x-text-94a3b8bf, rgba(148,163,184,0.75))', mt: 0.5 }}>
              {editingAgent
                ? 'Cambia el nombre, el contexto o el espacio de análisis que maneja este agente.'
                : 'Selecciona el espacio y modelo semántico que alimentarán el agente.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {editingAgent && (
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={handleCancelEdit}
                disabled={isSavingAgent}
                sx={{
                  borderColor: 'var(--wb-x-border-94a3b866, rgba(148, 163, 184, 0.4))',
                  color: 'var(--wb-x-text-cbd5e1, #cbd5e1)',
                  '&:hover': { borderColor: 'var(--wb-x-border-cbd5e1, #cbd5e1)' }
                }}
              >
                Cancelar edición
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={
                isSavingAgent ? (
                  <CircularProgress color="inherit" size={16} />
                ) : editingAgent ? (
                  <SaveIcon />
                ) : (
                  <AddIcon />
                )
              }
              onClick={handleSaveAgent}
              disabled={isSavingAgent || !formValues.workspaceId || !formValues.modelId}
              sx={{ bgcolor: 'var(--wb-x-bg-0095ff, #0095ff)', '&:hover': { bgcolor: 'var(--wb-x-bg-0277bd, #0277bd)' } }}
            >
              {editingAgent ? 'Guardar cambios' : 'Crear agente'}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del agente (opcional)"
              value={formValues.agentName}
              onChange={handleFormChange('agentName')}
              placeholder="agente-ventas"
              sx={{ mb: 1.5, ...darkTextFieldStyle }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="agentes" topic="configuracion-agente" anchor="nombre-del-agente-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 1.5 }}>
              <FormControl fullWidth size="small" sx={darkSelectStyle}>
                <InputLabel id="workspace-select-label" sx={darkInputLabelStyle}>Espacio de análisis</InputLabel>
                <Select
                  labelId="workspace-select-label"
                  value={formValues.workspaceId}
                  label="Espacio de análisis"
                  onChange={handleFormChange('workspaceId')}
                  disabled={isLoadingWorkspaces}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                >
                  {workspaces.map((workspace) => (
                    <MenuItem key={workspace.semantic_workspace_id} value={workspace.semantic_workspace_id}>
                      {workspace.name || workspace.semantic_workspace_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <HelpIcon module="agentes" topic="configuracion-agente" anchor="espacio-de-análisis" sx={{ mt: 0.5 }} />
            </Stack>
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 1.5 }}>
              <FormControl fullWidth size="small" required sx={darkSelectStyle}>
                <InputLabel id="model-select-label" sx={darkInputLabelStyle}>Modelo semántico</InputLabel>
                <Select
                  labelId="model-select-label"
                  value={formValues.modelId}
                  label="Modelo semántico"
                  onChange={handleFormChange('modelId')}
                  disabled={isLoadingModels || isLoadingWorkspaces || modelsForWorkspace.length === 0}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  {modelsForWorkspace.map((model) => (
                    <MenuItem key={model.semantic_model_id} value={model.semantic_model_id}>
                      {model.name || model.semantic_model_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <HelpIcon module="agentes" topic="configuracion-agente" anchor="modelo-semántico" sx={{ mt: 0.5 }} />
            </Stack>
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 1.5 }}>
              <FormControl fullWidth size="small" sx={darkSelectStyle}>
                <InputLabel id="kb-select-label" sx={darkInputLabelStyle}>Base de conocimiento (opcional)</InputLabel>
                <Select
                  labelId="kb-select-label"
                  value={formValues.knowledgeBaseId}
                  label="Base de conocimiento (opcional)"
                  onChange={handleFormChange('knowledgeBaseId')}
                  displayEmpty
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  <MenuItem value=""><em>Ninguna</em></MenuItem>
                  {knowledgeBases
                    .filter((kb) => kb.status === 'ACTIVE')
                    .map((kb) => (
                      <MenuItem key={kb.id} value={kb.id}>
                        {kb.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <HelpIcon module="agentes" topic="configuracion-agente" anchor="base-de-conocimiento-opcional" sx={{ mt: 0.5 }} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              minRows={5}
              size="small"
              label="Contexto del agente (opcional)"
              value={formValues.instruction}
              onChange={handleFormChange('instruction')}
              placeholder="Instrucciones/system prompt que sigue el agente al responder."
              helperText="Si lo dejas vacío se usa el contexto por defecto de WhisperBI."
              sx={{ mb: 1.5, ...darkTextFieldStyle }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="agentes" topic="configuracion-agente" anchor="contexto-del-agente-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <Box
              sx={{
                borderRadius: 2,
                border: '1px dashed var(--wb-x-border-94a3b84c, rgba(148, 163, 184, 0.3))',
                p: 2,
                background:
                  'linear-gradient(135deg, var(--wb-x-text-2563eb12, rgba(37,99,235,0.07)), var(--wb-x-text-0ea5e90d, rgba(14,165,233,0.05)))'
              }}
            >
              <Typography sx={{ color: 'var(--wb-x-text-cbd5e1, #cbd5e1)', fontWeight: 600, mb: 1 }}>
                Contexto de autenticación
              </Typography>
              <Stack spacing={1} sx={{ color: 'var(--wb-x-text-94a3b8d9, rgba(148,163,184,0.85))' }}>
                <Typography variant="body2">Cuenta: {currentAccountId || '—'}</Typography>
                <Typography variant="body2">Usuario: {currentUserId || '—'}</Typography>
                <Typography variant="body2">Compañía: {companyName || '—'}</Typography>
                <Typography variant="body2">
                  Workspace seleccionado: {formValues.workspaceId || '—'}
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <AgentsTable
        agents={pageRows}
        isLoading={isLoadingAgents}
        onDelete={handleDeleteRequest}
        onViewDetails={handleViewDetails}
        onEdit={handleEditAgent}
        onViewKnowledgeBase={handleViewKnowledgeBase}
        onReactivate={handleReactivate}
        reactivatingId={reactivatingId}
      />

      {!isLoadingAgents && agents.length > 0 && (
        <TablePaginationBar {...paginationProps} />
      )}

      <Typography variant="caption" sx={{ color: 'var(--wb-x-text-94a3b8b2, rgba(148,163,184,0.7))', display: 'block', mt: 2 }}>
        Última actualización visualizada: {lastUpdated ? lastUpdated.toLocaleString() : '—'}
      </Typography>

      {/* Diálogo de detalles del agente */}
      <AgentDetailsDialog
        open={Boolean(agentToView)}
        onClose={() => setAgentToView(null)}
        agent={agentToView}
      />

      {/* Detalle de la base de conocimiento ligada al agente (misma vista que
          la pantalla "Bases de conocimiento" -- sin identificadores de AWS). */}
      <KnowledgeBaseAdminDialog
        open={Boolean(kbRefToView)}
        onClose={() => setKbRefToView(null)}
        knowledgeBaseId={kbRefToView}
      />

      {/* Diálogo de carga durante creación/edición de agente */}
      <Dialog
        open={isSavingAgent}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            backgroundColor: 'var(--wb-bg-surface-alt)',
            color: 'var(--wb-text)',
            minWidth: { xs: '90%', sm: 400 }
          }
        }}
      >
        <DialogContent>
          <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={60} sx={{ color: 'var(--wb-primary)' }} />
            <Box textAlign="center">
              <Typography variant="h6" sx={{ color: 'var(--wb-text)', fontWeight: 600, mb: 1 }}>
                {editingAgent ? 'Guardando cambios' : 'Creando agente'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--wb-muted-85)' }}>
                {editingAgent
                  ? 'Se está actualizando el agente y, si cambiaste el workspace, sus conexiones y base de conocimiento.'
                  : 'Se está creando el agente y su base de conocimiento.'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--wb-muted-85)' }}>
                Por favor espere, este proceso puede tardar unos minutos.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Confirmación al reasignar el workspace de un agente ya creado */}
      <Dialog open={confirmReassignOpen} onClose={() => setConfirmReassignOpen(false)}>
        <DialogTitle>Reasignar espacio de análisis</DialogTitle>
        <DialogContent>
          <Typography>
            Vas a cambiar el espacio de análisis que maneja este agente. Sus
            conexiones y base de conocimiento se recalcularán automáticamente
            a partir del nuevo workspace. ¿Deseas continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReassignOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmReassign}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aviso: el nuevo agente reemplaza al ACTIVE del mismo (workspace, modelo) */}
      <Dialog open={Boolean(replaceTarget)} onClose={() => setReplaceTarget(null)}>
        <DialogTitle>Ya existe un agente para este modelo</DialogTitle>
        <DialogContent>
          <Typography>
            El modelo semántico ya tiene un agente activo
            {replaceTarget?.agentName ? <> (<strong>{replaceTarget.agentName}</strong>)</> : null}.
            Al guardar, ese agente quedará <strong>inactivo</strong> y este será el que use el chat.
            Podrás reactivarlo después desde la lista. ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceTarget(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmReplace}>Reemplazar</Button>
        </DialogActions>
      </Dialog>

      {/* Ofrecer reactivar el agente anterior tras borrar el ACTIVE de un modelo */}
      <Dialog open={Boolean(reactivateCandidate)} onClose={() => setReactivateCandidate(null)}>
        <DialogTitle>Reactivar el agente anterior</DialogTitle>
        <DialogContent>
          <Typography>
            El modelo <strong>{reactivateCandidate?.semanticModelName || reactivateCandidate?.semanticModelId}</strong> quedó
            sin agente activo. ¿Reactivar <strong>{reactivateCandidate?.agentName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateCandidate(null)}>Ahora no</Button>
          <Button
            variant="contained"
            onClick={() => handleReactivate(reactivateCandidate)}
            disabled={Boolean(reactivatingId)}
          >
            Reactivar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(agentToDelete)} onClose={() => !isDeletingAgent && setAgentToDelete(null)}>
        <DialogTitle>Eliminar agente</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Confirma eliminar el agente {agentToDelete?.agentName || agentToDelete?.agentId || ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentToDelete(null)} disabled={isDeletingAgent}>
            Cancelar
          </Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeletingAgent}
            startIcon={isDeletingAgent ? <CircularProgress color="inherit" size={16} /> : null}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success'
                ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning'
                ? 'var(--wb-warning-90)'
                : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

export default AgentsOverview;
